import React, { useCallback, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import isEmpty from 'lodash/isEmpty'
import { FormGenerator, FormInput, SelectOption } from '@microservices/wiskey-react-components'
import { Box, Divider } from '@mui/material'

import { BindTypeInputs } from '@components/BindTypeInputs'
import { INPUT_TYPE } from '@components/BindTypeInputs/ValueInputFactory'
import { Tab, TabPanel, Tabs } from '@components/Tabs'

import { getParamsString } from '@helpers'
import { GENERATOR_INPUT_TYPE } from '@constants'
import { ConstFormat } from '@formats'

import {
  BACKGROUND_BINDING_TYPE,
  GANTT_BIND_TYPE_LIST,
  UNIT_INTERVAL_OPTIONS,
} from '@gantt/components/GanttCreateOrEdit'
import { BaseFormFields } from '@gantt/components/GanttCreateOrEdit/components/GanttConfiguration/components'
import {
  displayToggleOptions,
  GANTT_MAX_ROW_HEIGHT,
  ganttConfigDefaultValue,
} from '@gantt/constants'
import { getBindTypeOptionsByStrings } from '@gantt/helpers'

import { useGanttParametersForm } from '../../hooks/useGanttParametersForm'

export const unitIntervalSelectOptions: SelectOption[] = [
  // UNIT_INTERVAL_OPTIONS.MINUTE,
  // UNIT_INTERVAL_OPTIONS.QUARTER,
  // UNIT_INTERVAL_OPTIONS.HALF,
  UNIT_INTERVAL_OPTIONS.HOUR,
  // UNIT_INTERVAL_OPTIONS.DAY,
  UNIT_INTERVAL_OPTIONS.WEEK,
  // todo скрыты до исправления WF-2657 и WF-4303
  // UNIT_INTERVAL_OPTIONS.MONTH,
  UNIT_INTERVAL_OPTIONS.YEAR,
].map(id => ({ id, name: i18next.t(`ganttCreate.unitIntervalOptions.${id}`), value: id }))

const backgroundBindingTypeOptions = getBindTypeOptionsByStrings([BACKGROUND_BINDING_TYPE.STATIC])
const cursorVerticalGuideLineBindingTypeOptions = getBindTypeOptionsByStrings([
  GANTT_BIND_TYPE_LIST.STATIC,
  GANTT_BIND_TYPE_LIST.JS,
])

const colorHints = {
  js: i18next.t('ganttCreate.timelineForm.hint.backgroundColorScriptHint'),
  static: i18next.t('ganttCreate.timelineForm.hint.backgroundColorStaticHint'),
}

export enum backgroundGridLineTypeEnum {
  SOLID = 'solid',
  DASHED = 'dashed',
}

export type backgroundGridLineType =
  | backgroundGridLineTypeEnum.SOLID
  | backgroundGridLineTypeEnum.DASHED

export const GanttParametersForm = () => {
  const { t } = useTranslation()
  const { handlers, state, methods } = useGanttParametersForm(unitIntervalSelectOptions)
  const { watch } = methods
  const { handleTabChange } = handlers

  const [isShowScriptDialog, setIsShowScriptDialog] = useState(false)

  const isEmptyField = (key: string) => isEmpty(getParamsString(watch(key)))

  const isPrimaryLineEnabled: boolean = watch(
    'canvas.backgroundGridAppearance.primaryLine.displayValue'
  )
  const isSecondaryLineEnabled: boolean = watch(
    'canvas.backgroundGridAppearance.secondaryLine.displayValue'
  )
  const isHorizontalLineEnabled: boolean = watch(
    'canvas.backgroundGridAppearance.horizontalLine.displayValue'
  )
  const primaryLineType: backgroundGridLineType = watch(
    'canvas.backgroundGridAppearance.primaryLine.type'
  )
  const secondaryLineType: backgroundGridLineType = watch(
    'canvas.backgroundGridAppearance.secondaryLine.type'
  )
  const horizontalLineType: backgroundGridLineType = watch(
    'canvas.backgroundGridAppearance.horizontalLine.type'
  )

  const toggleOpenScriptValueDialog = useCallback(() => {
    setIsShowScriptDialog(prevState => !prevState)
  }, [])

  const { currentTab, generalInputs, axisXInputs, axisYInputs, planningInputs } = state

  const hiddenParametersFields = new Map([
    ['canvas.axisX.title', true],
    ['canvas.axisX.tooltip', true],
    ['canvas.axisX.background', true],
    ['canvas.axisX.parameters', true],
    ['canvas.axisX.segmentHeight', true],
  ])

  const backgroundGridAppearanceTypeOptions: SelectOption[] = [
    { id: 0, value: 'dashed', name: 'dashed' },
    { id: 1, value: 'solid', name: 'solid' },
  ]

  const getColorHints = (isEmptyValue?: boolean) => {
    return {
      js: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : colorHints.js,
      static: isEmptyValue
        ? i18next.t('ganttCreate.timelineForm.hint.defaultHint')
        : colorHints.static,
    }
  }

  const getWidthHints = useCallback((isEmptyValue?: boolean) => {
    return {
      js: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
      static: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
    }
  }, [])

  const getTypeHints = useCallback((isEmptyValue?: boolean) => {
    return {
      js: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
      static: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
    }
  }, [])

  return (
    <Box p={2} pt={0}>
      <FormProvider {...methods}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label={t('ganttCreate.generalForm.tabs.general')} />
            <Tab label={t('ganttCreate.generalForm.tabs.axisX')} />
            <Tab label={t('ganttCreate.generalForm.tabs.axisY')} />
          </Tabs>
        </Box>
        <TabPanel index={0} sx={{ p: 1 }} value={currentTab}>
          <FormInput
            inputType={GENERATOR_INPUT_TYPE.INPUT}
            label={i18next.t('ganttCreate.generalForm.labels.rowHeight')}
            name='canvas.rowHeight'
            placeholder={i18next.t('ganttCreate.generalForm.placeholders.rowHeight')}
            textFieldInputProps={{
              inputComponent: ConstFormat as any,
              inputProps: { max: GANTT_MAX_ROW_HEIGHT },
            }}
          />
          <Box component='fieldset' mb={1}>
            <legend>{t('ganttCreate.generalForm.labels.yAxis')}</legend>
            <FormInput
              formLabelSx={{ width: '120px' }}
              inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
              label={i18next.t('ganttCreate.generalForm.labels.resizableY')}
              labelPlacement='end'
              name='canvas.isResizableY'
              labelSx={{
                minWidth: 'auto',
              }}
            />
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={t('ganttCreate.generalForm.labels.yAxisWidth')}
              name='canvas.yAxisWidth'
              placeholder={t('ganttCreate.generalForm.placeholders.yAxisWidth')}
              type={'number'}
              textFieldInputProps={{
                inputComponent: ConstFormat as any,
                inputProps: { max: 500 },
              }}
            />
          </Box>
          <Box component='fieldset' mb={1}>
            <legend>{t('ganttCreate.generalForm.labels.backlog')}</legend>
            <FormGenerator inputs={generalInputs} sx={{ flexDirection: 'column' }} />
          </Box>
          <Box component='fieldset' mb={1}>
            <legend>{t('ganttCreate.generalForm.labels.backgroundGridAppearance')}</legend>
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <legend>
              {t('ganttCreate.parametersForm.labels.backgroundGridAppearance.primaryLine')}
            </legend>
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <FormInput
              formLabelSx={{ width: '90px' }}
              inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
              label={i18next.t('form.show')}
              labelPlacement='end'
              name='canvas.backgroundGridAppearance.primaryLine.displayValue'
              labelSx={{
                minWidth: 'auto',
              }}
            />
            {isPrimaryLineEnabled && (
              <>
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.SELECT}
                  label={i18next.t('label.bindType')}
                  name={'canvas.backgroundGridAppearance.primaryLine.type'}
                  selectOptions={backgroundGridAppearanceTypeOptions}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.primaryLine.width'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.width'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.width'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 1, max: 3 },
                  }}
                />
                <BindTypeInputs
                  bindTypeOptions={backgroundBindingTypeOptions}
                  containerName='canvas.backgroundGridAppearance.primaryLine.color'
                  inputType={INPUT_TYPE.COLOR}
                  clearColor={
                    ganttConfigDefaultValue.canvas.backgroundGridAppearance.primaryLine.color.static
                  }
                  valueInputLabel={t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.color'
                  )}
                />
                <FormInput
                  disabled={primaryLineType === backgroundGridLineTypeEnum.SOLID}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.primaryLine.spacing'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.spacing'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.spacing'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 0 },
                  }}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.primaryLine.opacity'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.opacity'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.opacity'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 0, max: 1 },
                  }}
                />
              </>
            )}
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <legend>
              {t('ganttCreate.parametersForm.labels.backgroundGridAppearance.secondaryLine')}
            </legend>
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <FormInput
              formLabelSx={{ width: '90px' }}
              inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
              label={i18next.t('form.show')}
              labelPlacement='end'
              name='canvas.backgroundGridAppearance.secondaryLine.displayValue'
              labelSx={{
                minWidth: 'auto',
              }}
            />
            {isSecondaryLineEnabled && (
              <>
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.SELECT}
                  label={i18next.t('label.bindType')}
                  name={'canvas.backgroundGridAppearance.secondaryLine.type'}
                  selectOptions={backgroundGridAppearanceTypeOptions}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.secondaryLine.width'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.width'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.width'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 1, max: 3 },
                  }}
                />
                <BindTypeInputs
                  bindTypeOptions={backgroundBindingTypeOptions}
                  containerName='canvas.backgroundGridAppearance.secondaryLine.color'
                  inputType={INPUT_TYPE.COLOR}
                  clearColor={
                    ganttConfigDefaultValue.canvas.backgroundGridAppearance.secondaryLine.color
                      .static
                  }
                  valueInputLabel={t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.color'
                  )}
                />
                <FormInput
                  disabled={secondaryLineType === backgroundGridLineTypeEnum.SOLID}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.secondaryLine.spacing'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.spacing'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.spacing'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 0 },
                  }}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.secondaryLine.opacity'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.opacity'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.opacity'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 0, max: 1 },
                  }}
                />
              </>
            )}
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <legend>
              {t('ganttCreate.parametersForm.labels.backgroundGridAppearance.horizontalLine')}
            </legend>
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <FormInput
              formLabelSx={{ width: '90px' }}
              inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
              label={i18next.t('form.show')}
              labelPlacement='end'
              name='canvas.backgroundGridAppearance.horizontalLine.displayValue'
              labelSx={{
                minWidth: 'auto',
              }}
            />
            {isHorizontalLineEnabled && (
              <>
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.SELECT}
                  label={i18next.t('label.bindType')}
                  name={'canvas.backgroundGridAppearance.horizontalLine.type'}
                  selectOptions={backgroundGridAppearanceTypeOptions}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.horizontalLine.width'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.width'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.width'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 1, max: 3 },
                  }}
                />
                <BindTypeInputs
                  bindTypeOptions={backgroundBindingTypeOptions}
                  containerName='canvas.backgroundGridAppearance.horizontalLine.color'
                  inputType={INPUT_TYPE.COLOR}
                  clearColor={
                    ganttConfigDefaultValue.canvas.backgroundGridAppearance.horizontalLine.color
                      .static
                  }
                  valueInputLabel={t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.color'
                  )}
                />
                <FormInput
                  disabled={horizontalLineType === backgroundGridLineTypeEnum.SOLID}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.horizontalLine.spacing'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.spacing'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.spacing'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 0 },
                  }}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name='canvas.backgroundGridAppearance.horizontalLine.opacity'
                  label={i18next.t(
                    'ganttCreate.parametersForm.labels.backgroundGridAppearance.opacity'
                  )}
                  placeholder={i18next.t(
                    'ganttCreate.parametersForm.placeholders.backgroundGridAppearance.opacity'
                  )}
                  textFieldInputProps={{
                    inputComponent: ConstFormat as any,
                    inputProps: { min: 0, max: 1 },
                  }}
                />
              </>
            )}
          </Box>
        </TabPanel>
        <TabPanel index={1} sx={{ p: 1 }} value={currentTab}>
          <Box mb={1}>
            <legend>{t('ganttCreate.generalForm.timeVisualizationSection')}</legend>
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
            <Box component='fieldset' mb={1}>
              <legend>{t('ganttCreate.parametersForm.labels.toolbarSettings.blockLabel')}</legend>
              <FormGenerator inputs={axisXInputs} />
            </Box>
            <Box component='fieldset' mb={1}>
              <legend>{t('ganttCreate.parametersForm.labels.planning.blockLabel')}</legend>
              <FormGenerator inputs={planningInputs} />
            </Box>
            <Box component='fieldset' mb={1}>
              <legend>{t('ganttCreate.parametersForm.labels.realtimeLine.blockLabel')}</legend>
              <FormInput
                formLabelSx={{ width: '145px' }}
                inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                label={t('ganttCreate.parametersForm.labels.realtimeLine.hasRealtimeLine')} //
                labelPlacement='end'
                name='canvas.axisX.hasRealtimeLine'
                labelSx={{
                  minWidth: 'auto',
                }}
              />
              {watch('canvas.axisX.hasRealtimeLine') && (
                <>
                  <FormInput
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    label={i18next.t('ganttCreate.generalForm.labels.realtimeLineWidth')}
                    name='canvas.axisX.realtimeLineWidth'
                    value={watch('canvas.axisX.realtimeLineWidth')}
                    placeholder={i18next.t(
                      'ganttCreate.generalForm.placeholders.realtimeLineWidth'
                    )}
                    textFieldInputProps={{
                      inputComponent: ConstFormat as any,
                      value: watch('canvas.axisX.realtimeLineWidth'),
                      inputProps: { decimalScale: 1 },
                    }}
                  />
                  <BindTypeInputs
                    bindTypeOptions={backgroundBindingTypeOptions}
                    containerName='canvas.axisX.realtimeLineColor'
                    inputType={INPUT_TYPE.COLOR}
                    valueInputLabel={t('ganttCreate.parametersForm.labels.realtimeLine.color')}
                  />
                </>
              )}
            </Box>
            <BindTypeInputs
              isFieldsetMarkup
              bindTypeOptions={backgroundBindingTypeOptions}
              containerName='canvas.axisX.pastBackgroundColor'
              fieldsetTitle={t('ganttCreate.generalForm.labels.pastBackgroundColor')}
              inputType={INPUT_TYPE.COLOR}
            />
            <BindTypeInputs
              isFieldsetMarkup
              bindTypeOptions={backgroundBindingTypeOptions}
              containerName='canvas.axisX.futureBackgroundColor'
              fieldsetTitle={t('ganttCreate.generalForm.labels.futureBackgroundColor')}
              inputType={INPUT_TYPE.COLOR}
            />
            <Box component='fieldset'>
              <legend>
                {t('ganttCreate.parametersForm.labels.cursorVerticalGuideLine.blockLabel')}
              </legend>
              <BindTypeInputs
                bindTypeOptions={cursorVerticalGuideLineBindingTypeOptions}
                blockLabel={t('ganttCreate.parametersForm.labels.cursorVerticalGuideLine.has')}
                containerName='canvas.axisX.cursorVerticalGuideLine.has'
                inputType={INPUT_TYPE.TOGGLE}
                toggleButton={{
                  toggleButtonOptions: displayToggleOptions,
                }}
              />
              <BindTypeInputs
                isShownBinding
                bindTypeOptions={cursorVerticalGuideLineBindingTypeOptions}
                blockLabel={t('ganttCreate.parametersForm.labels.cursorVerticalGuideLine.color')}
                containerName='canvas.axisX.cursorVerticalGuideLine.color'
                hintDict={getColorHints(isEmptyField('canvas.axisX.cursorVerticalGuideLine.color'))}
                inputType={INPUT_TYPE.COLOR}
              />
              <BindTypeInputs
                isShownBinding
                bindTypeOptions={cursorVerticalGuideLineBindingTypeOptions}
                blockLabel={t('ganttCreate.parametersForm.labels.cursorVerticalGuideLine.width')}
                containerName='canvas.axisX.cursorVerticalGuideLine.width'
                hintDict={getTypeHints(isEmptyField('canvas.axisX.cursorVerticalGuideLine.width'))}
                inputType={INPUT_TYPE.NUMBER}
              />
              <BindTypeInputs
                isShownBinding
                bindTypeOptions={cursorVerticalGuideLineBindingTypeOptions}
                blockLabel={t('ganttCreate.parametersForm.labels.cursorVerticalGuideLine.type')}
                containerName='canvas.axisX.cursorVerticalGuideLine.type'
              />
            </Box>
            <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0 }} />
          </Box>
          <legend>{t('ganttCreate.parametersForm.boxVisualizationSection')}</legend>
          <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0, mb: 1 }} />
          <BaseFormFields
            hiddenFields={hiddenParametersFields}
            isShowScriptDialog={isShowScriptDialog}
            mainPath={'canvas.axisX'}
            toggleOpenScriptValueDialog={toggleOpenScriptValueDialog}
          />
        </TabPanel>
        <TabPanel index={2} sx={{ p: 1 }} value={currentTab}>
          <FormGenerator inputs={axisYInputs} />
          <legend>{t('ganttCreate.generalForm.rowExpansion')}</legend>
          <Divider orientation='horizontal' sx={{ width: 1, mx: 0.2, p: 0 }} />
          <FormInput
            formLabelSx={{ width: '140px' }}
            inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
            label={i18next.t('ganttCreate.generalForm.labels.rowExpansion')}
            labelPlacement={'end'}
            name={'canvas.hasRowExpansion'}
            labelSx={{
              minWidth: 'auto',
            }}
          />
          <FormInput
            formLabelSx={{ width: '230px' }}
            inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
            label={i18next.t('ganttCreate.generalForm.labels.rowsExpansionAtStart')}
            labelPlacement={'end'}
            labelSx={{ width: 'auto' }}
            name={'canvas.rowsExpansionAtStart'}
          />
        </TabPanel>
      </FormProvider>
    </Box>
  )
}
